import React, { useEffect, useState } from "react";
import {
  FeatherCheck,
  FeatherAlertTriangle,
  FeatherAlertCircle,
  FeatherInfo,
  FeatherX,
} from "@subframe/core";
import { Toast as ToastType, ToastVariant } from "./ToastContext";

interface ToastProps {
  toast: ToastType;
  onDismiss: (id: string) => void;
}

const variantStyles: Record<
  ToastVariant,
  {
    bg: string;
    border: string;
    text: string;
    icon: React.ReactNode;
  }
> = {
  success: {
    bg: "bg-success-50",
    border: "border-success-200",
    text: "text-success-700",
    icon: <FeatherCheck className="h-5 w-5" />,
  },
  error: {
    bg: "bg-error-50",
    border: "border-error-200",
    text: "text-error-700",
    icon: <FeatherAlertCircle className="h-5 w-5" />,
  },
  warning: {
    bg: "bg-warning-50",
    border: "border-warning-200",
    text: "text-warning-700",
    icon: <FeatherAlertTriangle className="h-5 w-5" />,
  },
  info: {
    bg: "bg-brand-50",
    border: "border-brand-200",
    text: "text-brand-700",
    icon: <FeatherInfo className="h-5 w-5" />,
  },
};

export const Toast: React.FC<ToastProps> = ({ toast, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const style = variantStyles[toast.variant];

  // Fade in animation
  useEffect(() => {
    setTimeout(() => setIsVisible(true), 10);
  }, []);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => {
      onDismiss(toast.id);
    }, 200); // Match animation duration
  };

  return (
    <div
      className={`
        flex items-center gap-3 rounded-lg border border-solid px-4 py-3 shadow-lg min-w-[320px] max-w-[500px]
        transition-all duration-200 transform
        ${style.bg} ${style.border}
        ${isVisible && !isExiting ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4"}
      `}
      role="alert"
    >
      <div className={`flex-none ${style.text}`}>{style.icon}</div>
      <span className={`text-body font-body ${style.text} flex-1`}>
        {toast.message}
      </span>
      <button
        type="button"
        onClick={handleDismiss}
        className={`flex-none ${style.text} hover:opacity-70 transition-opacity`}
        aria-label="Dismiss"
      >
        <FeatherX className="h-4 w-4" />
      </button>
    </div>
  );
};

